import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { Switch } from '@material-ui/core';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import welcomeImage from 'assets/images/hello.svg';
import LoadingComponent from 'components/LoadingComponent';
import { ArticleIcon } from 'components/TableFrame';
import { MoreVertButton } from 'components/buttons/MoreVertButton';
import ImageBadge from 'components/images/ImageBadge';
import { BuildingStatusIndicator } from 'components/common/BuildingStatusIndicator';
import { DeleteConfirmationModal } from 'components/modals/DeleteConfirmationModal';
import { ContentUsageModal } from 'components/modals/ContentUsageModal';
import { AddToCourseModal } from 'components/modals/AddToCourseModal';
import { Pagination } from 'components/reports/controls/Pagination';
import { TableBody } from 'components/tables/learner-stats/TableBody';
import { TableHeader } from 'components/tables/learner-stats/TableHeader';
import { SortArrow, TableHeaderContainer, TableHeaderText } from 'components/tables/learner-stats/elements';
import { TableSearchBar } from 'components/tables/TableSearchBar';
import { Color } from 'core';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useGetSets } from 'hooks/useGetSets';
import useMutateSet from 'hooks/useMutateSet';
import useDeleteSet from 'hooks/useDeleteSet';
import { PRIVACY_TYPES } from 'models/assignment';
import moment from 'moment';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { formatStringForSortComparison } from 'utils/tableUtils';
import { getEditSetUrlForReactRouter, getPreviewSetUrlForReactRouter } from 'utils/pathUtils';

interface ContentTableProps {
  registerSortReset?: (resetSort: () => void) => void;
}

export const ContentTable = (props: ContentTableProps = {}) => {
  const { registerSortReset } = props;
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [optimisticSharedStates, setOptimisticSharedStates] = useState<{ [key: string]: boolean }>({});
  const [showActionsMenu, setShowActionsMenu] = useState<{ [key: string]: boolean }>({});
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedSetForDelete, setSelectedSetForDelete] = useState<any>(null);
  const [showUsedInModal, setShowUsedInModal] = useState<boolean>(false);
  const [selectedSetForUsedIn, setSelectedSetForUsedIn] = useState<any>(null);
  const [showAddToCourseModal, setShowAddToCourseModal] = useState<boolean>(false);
  const [selectedSetForAddToCourse, setSelectedSetForAddToCourse] = useState<any>(null);

  // Pagination state
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Search state
  const [searchName, setSearchName] = useState<string>('');

  const { t } = useTranslation();
  const user = useCurrentUser();
  const primaryPartnerId = user?.primaryPartner.id;

  const resetSort = () => {
    setSortColumn('');
    setSortDirection('asc');
    setPageNumber(1);
  };

  useEffect(() => {
    if (registerSortReset) {
      registerSortReset(resetSort);
    }
  }, [registerSortReset]);

  // Map frontend column names to API field names and create sort parameter
  const getSortParam = () => {
    if (!sortColumn) return '-updated_at'; // Default sort

    const sortMapping: { [key: string]: string } = {
      title: 'name',
      last_updated: 'updated_at',
    };

    const apiSortColumn = sortMapping[sortColumn] || sortColumn;
    return sortDirection === 'desc' ? `-${apiSortColumn}` : apiSortColumn;
  };

  const {
    data,
    status: queryStatus,
    refetch,
  } = useGetSets(primaryPartnerId, true, pageNumber, pageSize, getSortParam(), searchName);
  const sets = data?.data || [];
  const setsMeta = data?.meta;
  // Pagination calculations
  const totalCount = setsMeta ? setsMeta['total-count'] : 0;
  const totalPages = setsMeta ? setsMeta['total-pages'] : 0;

  // Check if any sets are building
  const hasBuildingSets = sets.some((set) => set.state === 'building');

  // Set up polling for building sets
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (hasBuildingSets) {
      intervalId = setInterval(() => {
        refetch();
      }, 10000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [hasBuildingSets, refetch]);

  // Pagination handlers
  const goToPageOne = () => setPageNumber(1);
  const goToNextPage = () => pageNumber < totalPages && setPageNumber(pageNumber + 1);
  const goToPreviousPage = () => pageNumber > 1 && setPageNumber(pageNumber - 1);
  const goToLastPage = () => setPageNumber(totalPages);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1);
  };

  // Reset page when search changes
  useEffect(() => {
    setPageNumber(1);
  }, [searchName]);

  const [mutateSet] = useMutateSet();
  const [deleteSet, { isLoading: isDeleting }] = useDeleteSet();

  const toggleSortDirection = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };

  const getTypeDisplay = (goalType: string) => {
    const typeMap: { [key: string]: string } = {
      set: t('Learning Set'),
      survey: t('Survey'),
      assessment: t('Assessment'),
      scorm: t('SCORM'),
    };
    return typeMap[goalType] || goalType;
  };

  const getTypePillColor = (goalType: string) => {
    const colorMap: { [key: string]: { background: string; color: string } } = {
      set: { background: '#e3f2fd', color: '#1976d2' }, // Blue
      survey: { background: '#f3e5f5', color: '#7b1fa2' }, // Purple
      assessment: { background: '#e8f5e8', color: '#388e3c' }, // Green
      scorm: { background: '#fce4ec', color: '#c2185b' }, // Reddish
    };
    return colorMap[goalType] || { background: '#f5f5f5', color: '#666' };
  };

  const getCourseCountText = (courses: any[]) => {
    const count = courses?.length || 0;
    if (count === 0) return 'No courses';
    if (count === 1) return '1 course';
    return `${count} courses`;
  };

  const getDeleteConfirmationBody = (set: any) => {
    const courseCount = set.courses?.length || 0;
    let usageText: string;
    if (courseCount === 0) {
      usageText = 'This assignment is not used in any courses.';
    } else if (courseCount === 1) {
      usageText = 'This assignment is used in 1 course.';
    } else {
      usageText = `This assignment is used in ${courseCount} courses.`;
    }
    return `${usageText} Deleting will permanently remove the assignment.`;
  };

  const getSharedState = (set: any): boolean => {
    return optimisticSharedStates.hasOwnProperty(set.id) ? optimisticSharedStates[set.id] : set.isShared || false;
  };

  const toggleSetPrivacy = (set: any): void => {
    if (!set) return;
    const currentSharedState = getSharedState(set);
    const newSharedState = !currentSharedState;

    // Optimistically update the UI
    setOptimisticSharedStates((prev) => ({
      ...prev,
      [set.id]: newSharedState,
    }));

    const privacyTypeId = newSharedState ? PRIVACY_TYPES.COURSES_ONLY : PRIVACY_TYPES.PRIVATE_ONLY;
    mutateSet({
      id: set.id,
      payload: { data: { attributes: { privacy_type_id: privacyTypeId } } },
    });
  };

  const toggleActionsMenu = (setId: string): void => {
    setShowActionsMenu((prev) => ({
      ...prev,
      [setId]: !prev[setId],
    }));
  };

  const handleDeleteClick = (set: any): void => {
    setSelectedSetForDelete(set);
    setShowDeleteModal(true);
    // Close the actions menu
    setShowActionsMenu((prev) => ({
      ...prev,
      [set.id]: false,
    }));
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!selectedSetForDelete) return;

    try {
      await deleteSet({ setId: selectedSetForDelete.id });
      setShowDeleteModal(false);
      setSelectedSetForDelete(null);
    } catch (error) {
      console.error('Failed to delete set:', error);
    }
  };

  const handleDeleteCancel = (): void => {
    setShowDeleteModal(false);
    setSelectedSetForDelete(null);
  };

  const handleUsedInClick = (set: any): void => {
    setSelectedSetForUsedIn(set);
    setShowUsedInModal(true);
  };

  const handleUsedInModalClose = (): void => {
    setShowUsedInModal(false);
    setSelectedSetForUsedIn(null);
  };

  const handleAddToCourseClick = (set: any): void => {
    setSelectedSetForAddToCourse(set);
    setShowAddToCourseModal(true);
    // Close the actions menu
    setShowActionsMenu((prev) => ({
      ...prev,
      [set.id]: false,
    }));
  };

  const handleAddToCourseModalClose = (): void => {
    setShowAddToCourseModal(false);
    setSelectedSetForAddToCourse(null);
    refetch(); // Refresh the content table to reflect course assignment changes
  };

  const noSetsMessage = (
    <div style={{ color: Color.darkGray, textAlign: 'center' }}>
      <ArticleIcon src={welcomeImage} style={{ marginTop: '3em' }} />
      <h2>No Content Yet</h2>
      <p>Get started creating your first content.</p>
    </div>
  );

  const errorMessage = (
    <div style={{ color: Color.red, textAlign: 'center' }}>
      <h2>Error Loading Content</h2>
      <p>There was a problem loading your content. Please try refreshing the page.</p>
    </div>
  );

  const columns: ColumnDef<any>[] = [
    {
      id: 'title',
      accessorFn: (row) => row.name,
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <SContentImageContainer style={{ flex: 1 }}>
              <ImageBadge image={rowData.image} style={contentImageStyles} entity="set" />
            </SContentImageContainer>
            <div style={{ flex: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              {rowData.state === 'building' && <BuildingStatusIndicator />}
              {rowData.goalType !== 'scorm' && rowData.state !== 'building' ? (
                <TitleLink to={getEditSetUrlForReactRouter(rowData.learnVersion, rowData.id)}>{rowData.name}</TitleLink>
              ) : (
                <span style={{ fontWeight: 600, color: Color.primaryBlack }}>{rowData.name}</span>
              )}
            </div>
          </div>
        );
      },
      header: () => (
        <ColumnHeader
          title={t('Title')}
          sort={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('title')}
          isSortable={true}
        />
      ),
    },
    {
      id: 'type',
      accessorFn: (row) => row.goalType,
      cell: ({ row }) => {
        const rowData = row.original;
        const colors = getTypePillColor(rowData.goalType);
        return (
          <TypePill style={{ backgroundColor: colors.background, color: colors.color }}>
            {getTypeDisplay(rowData.goalType)}
          </TypePill>
        );
      },
      header: () => (
        <ColumnHeader title={t('Type')} sort={''} sortDirection={sortDirection} onSort={() => {}} isSortable={false} />
      ),
    },
    {
      id: 'shared',
      accessorFn: (row) => row.isShared,
      cell: ({ row }) => {
        const rowData = row.original;
        return <Switch checked={getSharedState(rowData)} onChange={() => toggleSetPrivacy(rowData)} color="primary" />;
      },
      header: () => (
        <ColumnHeader
          title={t('Shared')}
          sort={''}
          sortDirection={sortDirection}
          onSort={() => {}}
          isSortable={false}
        />
      ),
    },
    {
      id: 'used_in',
      accessorFn: (row) => row.courses?.length || 0,
      cell: ({ row }) => {
        const rowData = row.original;
        const courseCount = rowData.courses?.length || 0;
        const countText = getCourseCountText(rowData.courses);

        if (courseCount > 0) {
          return <UsedInLink onClick={() => handleUsedInClick(rowData)}>{countText}</UsedInLink>;
        }

        return <UsedInText>{countText}</UsedInText>;
      },
      header: () => (
        <ColumnHeader
          title={t('Used In')}
          sort={''}
          sortDirection={sortDirection}
          onSort={() => {}}
          isSortable={false}
        />
      ),
    },
    {
      id: 'last_updated',
      accessorFn: (row) => row.updatedAt,
      cell: ({ row }) => <LastUpdatedCell>{moment(row.original.updatedAt).format('MM/DD/YY')}</LastUpdatedCell>,
      header: () => (
        <ColumnHeader
          title={t('Last Updated')}
          sort={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('last_updated')}
          isSortable={true}
        />
      ),
    },
    {
      id: 'actions',
      header: () => (
        <TableHeaderContainer style={{ justifyContent: 'center' }}>
          <TableHeaderText>{t('Actions')}</TableHeaderText>
        </TableHeaderContainer>
      ),
      cell: ({ row }) => {
        const rowData = row.original;
        const isMenuOpen = showActionsMenu[rowData.id] || false;

        return (
          <ActionsContainer>
            <MoreVertButton
              click={() => toggleActionsMenu(rowData.id)}
              aria-controls={`content-actions-menu-${rowData.id}`}
            />
            {isMenuOpen && (
              <ActionsMenu onMouseLeave={() => toggleActionsMenu(rowData.id)} id={`content-actions-menu-${rowData.id}`}>
                {rowData.goalType !== 'scorm' && rowData.state !== 'building' && (
                  <ActionLink to={getPreviewSetUrlForReactRouter(rowData.learnVersion, rowData.id)}>Preview</ActionLink>
                )}
                {rowData.goalType !== 'scorm' && rowData.state !== 'building' && (
                  <ActionLink to={getEditSetUrlForReactRouter(rowData.learnVersion, rowData.id)}>Edit</ActionLink>
                )}
                {rowData.goalType !== 'survey' && (
                  <ActionButton onClick={() => handleAddToCourseClick(rowData)}>Add to course</ActionButton>
                )}
                <DeleteButton onClick={() => handleDeleteClick(rowData)}>Delete</DeleteButton>
              </ActionsMenu>
            )}
          </ActionsContainer>
        );
      },
    },
  ];

  const handleSortingChange = (sortBy: string) => {
    if (sortBy === sortColumn) {
      toggleSortDirection();
    } else {
      setSortColumn(sortBy);
      setSortDirection('asc');
    }
    // Reset to page 1 when sorting changes
    setPageNumber(1);
  };

  const table = useReactTable({
    data: sets,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (queryStatus === 'loading') {
    return <LoadingComponent component={<></>} isLoading={true} />;
  }

  if (queryStatus === 'error') {
    return (
      <div>
        <SearchContainer>
          <TableSearchBar
            value={searchName}
            onChange={setSearchName}
            resetPage={() => setPageNumber(1)}
            placeholder={t('Search content')}
          />
        </SearchContainer>
        <TableContainer>{errorMessage}</TableContainer>
      </div>
    );
  }

  return (
    <div>
      <SearchContainer>
        <TableSearchBar
          value={searchName}
          onChange={setSearchName}
          resetPage={() => setPageNumber(1)}
          placeholder={t('Search content')}
        />
      </SearchContainer>
      <TableContainer>
        <StyledTable>
          <TableHeader table={table} handleHeaderClick={() => {}} />
          <TableBody table={table} />
        </StyledTable>
        {sets.length === 0 && totalCount === 0 && noSetsMessage}
        {totalCount > 0 && (
          <Pagination
            totalCount={totalCount}
            page={pageNumber}
            pageSize={pageSize}
            goToPageOne={goToPageOne}
            goToPreviousPage={goToPreviousPage}
            goToNextPage={goToNextPage}
            goToLastPage={goToLastPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </TableContainer>

      {showDeleteModal && selectedSetForDelete && (
        <DeleteConfirmationModal
          title={`Are you sure you want to delete "${selectedSetForDelete.name}"?`}
          body={getDeleteConfirmationBody(selectedSetForDelete)}
          requireTextConfirmation={true}
          isDeleting={isDeleting}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {showUsedInModal && selectedSetForUsedIn && (
        <ContentUsageModal contentItem={selectedSetForUsedIn} onClose={handleUsedInModalClose} />
      )}

      {showAddToCourseModal && selectedSetForAddToCourse && (
        <AddToCourseModal contentItem={selectedSetForAddToCourse} onClose={handleAddToCourseModalClose} />
      )}
    </div>
  );
};

interface ColumnHeaderProps {
  title: string;
  sort: string;
  sortDirection: 'asc' | 'desc';
  onSort: () => void;
  isSortable?: boolean;
}

const ColumnHeader = ({ title, sort, sortDirection, onSort, isSortable = false }: ColumnHeaderProps) => {
  return (
    <TableHeaderContainer
      style={Object.assign({ justifyContent: 'left', whiteSpace: 'nowrap' }, isSortable ? { cursor: 'pointer' } : {})}
      onClick={isSortable ? onSort : undefined}
    >
      <TableHeaderText>{title}</TableHeaderText>
      {sort === formatStringForSortComparison(title) && (
        <SortArrow icon={sortDirection === 'desc' ? faArrowDown : faArrowUp} />
      )}
    </TableHeaderContainer>
  );
};

const SContentImageContainer = styled.div`
  display: inline-block;
`;

const contentImageStyles = {
  height: '32px',
  width: '32px',
  verticalAlign: 'middle',
  marginRight: '25px',
};

const TableContainer = styled.div``;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TypePill = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
`;

const UsedInLink = styled.button`
  background: none;
  border: none;
  color: ${Color.primaryBlue};
  cursor: pointer;
  text-decoration: underline;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  white-space: nowrap;

  &:hover {
    text-decoration: none;
  }

  &:focus {
    outline: 2px solid ${Color.primaryBlue};
    outline-offset: 2px;
  }
`;

const UsedInText = styled.span`
  white-space: nowrap;
`;

const LastUpdatedCell = styled.span`
  white-space: nowrap;
`;

const ActionsContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`;

const ActionsMenu = styled.div`
  position: absolute;
  top: 40px;
  right: 0;
  background: white;
  border: 1px solid ${Color.lightGray};
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
  padding: 4px;
  white-space: nowrap;

  > * {
    margin: 4px 0;
  }
`;

const baseActionStyles = `
  background: transparent;
  border: none;
  color: ${Color.darkGray};
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: 16px;
  text-decoration: none;
  text-align: left;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    background-color: #f8f9fa;
    text-decoration: none;
  }
`;

const ActionLink = styled(Link)`
  ${baseActionStyles}
`;

const ActionButton = styled.button`
  ${baseActionStyles}
`;

const DeleteButton = styled(ActionButton)`
  color: #dc3545;
`;

const SearchContainer = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid ${Color.lightGray};
`;

const TitleLink = styled(Link)`
  font-weight: 600;
  color: ${Color.primaryBlack};
  text-decoration: none;

  &:hover {
    text-decoration: underline !important;
    color: ${Color.primaryBlue} !important;
  }

  &:focus {
    outline: 2px solid ${Color.primaryBlue};
    outline-offset: 2px;
  }
`;
